import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Table from 'src/components/Table';
import {
    ExtendOptionsType,
    GridFilterType,
    GridSortType,
    ProductDetailGridTableRefType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType,
} from 'src/types/table';
import translate from '@translate';
import { ProductStockMovementsType } from 'src/types/productDetail';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import { colors } from 'src/themes/default';
import { ConstantsCodeType } from '../../types/constantsCodes';

const StyledContainer = styled('div')({
    padding: '16px 0',
});

const OPTIONS_SESSION_NAME = 'productStockMovementsTable';

const allColumns: TableColumnType[] = [
    {
        label: 'id',
        key: 'ID',
        type: 'id',
    },
    {
        label: 'createdAt',
        key: 'CREATED_AT',
        type: 'dateTime',
    },
    {
        label: 'type',
        key: 'TYPE',
        type: 'select',
        selectOption: 'stockMovementTypeCodes',
        customRender: true,
    },
    {
        label: 'count',
        key: 'COUNT',
        type: 'number-filter',
        customRender: true,
    },
    {
        label: 'expirationDate',
        key: 'EXPIRATION_DATE',
        type: 'date',
    },
    {
        label: 'lot',
        key: 'LOT',
        type: 'string',
    },
];

const DEFAULT_ITEMS = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'CREATED_AT', direction: 'DESC' },
    page: 1,
    items: DEFAULT_ITEMS,
    columns: [...allColumns],
};

function getInitialTableOptions(): TablePropsOptionsType {
    try {
        const session = JSON.parse(sessionStorage.getItem(OPTIONS_SESSION_NAME) || 'null');
        if (session && typeof session.items === 'number' && ROWS_PER_PAGE_OPTIONS.includes(session.items)) {
            return { ...initTableOptions, ...session, columns: session.columns || initTableOptions.columns };
        }
    } catch {
        // ignore invalid session
    }
    return initTableOptions;
}

export function buildStockMovementsGridPayload(
    options: TablePropsOptionsType,
    idProduct: string
): {
    page: number;
    items: number;
    sorts: GridSortType[];
    filters: GridFilterType[][];
    columns: string[];
} {
    const items = ROWS_PER_PAGE_OPTIONS.includes(options.items) ? options.items : DEFAULT_ITEMS;
    const userConditions = (options.filters || []).flat().filter((c) => c && c.column);
    const productFilter = { column: 'PRODUCT_ID', operator: 'eq' as const, value: idProduct };
    return {
        page: options.page,
        items,
        sorts: [options.sort],
        filters: [[...userConditions, productFilter]],
        columns: allColumns.map((c) => c.key),
    };
}

export const stockMovementsExportExtendOptions: ExtendOptionsType = {
    rowFunction: (row) => ({
        ...row,
        TYPE: row.TYPE ? translate(`productDetail.stockMovementType.${row.TYPE}`) : row.TYPE,
    }),
};

export interface ProductStockMovementsTablePropsType {
    data: ProductStockMovementsType | null;
    loading?: boolean;
    onFetchData: (payload: ReturnType<typeof buildStockMovementsGridPayload>) => void;
    idProduct: string;
    stockMovementTypeCodes: ConstantsCodeType[];
}

const ProductStockMovementsTable = forwardRef<ProductDetailGridTableRefType, ProductStockMovementsTablePropsType>(
    (props, ref) => {
        const tableRef = useRef<TableRefType>(null);
        const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(getInitialTableOptions);

        const rows: TableRowType[] = props.data?.data
            ? props.data.data.map(
                  (row): TableRowType => ({
                      ID: row.ID,
                      CREATED_AT: row.CREATED_AT,
                      TYPE: row.TYPE,
                      COUNT: row.COUNT,
                      EXPIRATION_DATE: row.EXPIRATION_DATE,
                      LOT: row.LOT,
                  })
              )
            : [];

        const totalCount = props.data?.totalCount ?? 0;

        useImperativeHandle(
            ref,
            (): ProductDetailGridTableRefType => ({
                exportTable: (type, filename, getPrintRows, extendedColmns) =>
                    tableRef.current?.exportTable(type, filename, getPrintRows, extendedColmns),
                exportTableGrid: (type, filename, getGridParams, requestUrl, extendedColmns, extendOptions) =>
                    tableRef.current?.exportTableGrid(
                        type,
                        filename,
                        getGridParams,
                        requestUrl,
                        extendedColmns,
                        extendOptions
                    ),
                getGridParams: () => buildStockMovementsGridPayload(tableOptions, props.idProduct),
            }),
            [tableOptions, props.idProduct]
        );

        useEffect(() => {
            if (props.idProduct) {
                props.onFetchData(buildStockMovementsGridPayload(tableOptions, props.idProduct));
            }
        }, []);

        const handleChangeOptions = useCallback(
            (newOptions: TablePropsOptionsType) => {
                setTableOptions(newOptions);
                props.onFetchData(buildStockMovementsGridPayload(newOptions, props.idProduct));
            },
            [props.onFetchData, props.idProduct]
        );

        const customRender = useCallback((column: TableColumnType, row: TableRowType) => {
            const value = row[column.key];
            switch (column.key) {
                case 'CREATED_AT':
                    return value ? moment(value as string).format('DD.MM.YYYY HH:mm') : '';
                case 'EXPIRATION_DATE':
                    return value ? moment(value as string).format('DD.MM.YYYY') : '';
                case 'TYPE':
                    return translate(`productDetail.stockMovementType.${value}`);
                case 'COUNT': {
                    const count = Number(value);
                    return (
                        <span style={{ color: count >= 0 ? colors.accent_O : colors.alert_O }}>
                            {count >= 0 ? '+' : ''}
                            {count}
                        </span>
                    );
                }
                default:
                    return value;
            }
        }, []);

        const tableSelectOptions: {
            [key: string]: ConstantsCodeType[];
        } = {
            stockMovementTypeCodes: props.stockMovementTypeCodes,
        };

        return (
            <StyledContainer>
                <Table
                    ref={tableRef}
                    rowIdKey="ID"
                    rows={rows}
                    allColumns={allColumns}
                    columsLabelPrefix="productDetail.stockMovementsTable"
                    options={tableOptions}
                    totalCount={totalCount}
                    loading={props.loading}
                    customRender={customRender}
                    heightVariant="top-tab"
                    fullHeight
                    optionsSessionName={OPTIONS_SESSION_NAME}
                    selectOptions={tableSelectOptions}
                    onChangeOptions={handleChangeOptions}
                    showClearFilters
                />
            </StyledContainer>
        );
    }
);

ProductStockMovementsTable.displayName = 'ProductStockMovementsTable';

export default ProductStockMovementsTable;
